'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { DevsuLogoComplete } from '@/assets/DevsuLogoComplete';

/**
 * Props for `ProfileGreetingCta`.
 */
export type ProfileGreetingCtaProps =
  SliceComponentProps<Content.ProfileGreetingCtaSlice>;

/**
 * Component for "ProfileGreetingCta" Slices.
 */
const ProfileGreetingCta: FC<ProfileGreetingCtaProps> = ({ slice }) => {
  const {
    profile_card,
    heading,
    description,
    ctas,
    client_logo,
    background_image,
  } = slice.primary;

  // Get first profile card item (it's a group so we take the first one)
  const profile = profile_card?.[0];

  // Animation refs
  const heroCardRef = useRef<HTMLDivElement>(null);
  const profileCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const heroElement = heroCardRef.current;
    const profileElement = profileCardRef.current;

    if (heroElement) {
      heroElement.style.transitionDelay = '0ms';
      observer.observe(heroElement);
    }
    if (profileElement) {
      profileElement.style.transitionDelay = '200ms';
      observer.observe(profileElement);
    }

    return () => {
      if (heroElement) {
        observer.unobserve(heroElement);
      }
      if (profileElement) {
        observer.unobserve(profileElement);
      }
    };
  }, []);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative w-full pt-[160px] pb-[60px] px-4 md:px-8 lg:px-[200px]"
      style={{
        backgroundImage:
          'linear-gradient(rgba(0, 0, 0, 0) 15.895%, rgba(14, 17, 16, 0.5) 100%), linear-gradient(rgb(15, 66, 58) 63.331%, rgb(14, 17, 16) 100%)',
      }}
    >
      <div className="flex flex-col gap-[60px] items-center max-w-[1440px] mx-auto">
        {/* Hero Card */}
        <div
          ref={heroCardRef}
          className="relative w-full max-w-[1040px] h-auto min-h-[250px] md:min-h-[350px] rounded-[24px] overflow-hidden shadow-[0px_20px_24px_-4px_rgba(0,0,0,0.08),0px_8px_8px_-4px_rgba(0,0,0,0.03)] opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {/* Background Image */}
          {isFilled.image(background_image) && (
            <div className="absolute inset-0 overflow-hidden">
              <PrismicNextImage
                field={background_image}
                className="absolute w-full h-[177%] top-0 left-0 object-cover"
                fill={false}
                width={1040}
                height={620}
              />
            </div>
          )}

          {/* Gradient Overlay */}
          <div
            className="absolute inset-0 rounded-[24px]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(0, 39, 38, 0.8) 0%, rgba(0, 0, 0, 0) 100%), linear-gradient(90deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%)',
            }}
          />

          {/* Content */}
          <div className="relative flex flex-col md:flex-row gap-6 items-center justify-between px-6 md:px-[60px] py-8 md:py-0 h-full min-h-[250px] md:min-h-[350px]">
            {/* Left side - Logo and Text */}
            <div className="flex flex-col gap-8 md:gap-[49px] items-start justify-center w-full md:w-auto md:max-w-[673px]">
              {/* Devsu Logo */}
              <DevsuLogoComplete width={136} height={31} />

              {/* Title and Subtitle */}
              <div className="flex flex-col gap-2 text-white w-full">
                {isFilled.richText(heading) && (
                  <div className="font-extrabold text-[28px] md:text-[40px] leading-[1.2]">
                    <PrismicRichText field={heading} />
                  </div>
                )}
                {isFilled.richText(description) && (
                  <div className="font-medium text-[16px] md:text-[20px] leading-[1.4] opacity-90">
                    <PrismicRichText field={description} />
                  </div>
                )}
              </div>
            </div>

            {/* Right side - Client Logo in circular container */}
            {isFilled.image(client_logo) && (
              <div className="flex items-center justify-center shrink-0 w-[140px] h-[140px] md:w-[200px] md:h-[200px] rounded-full bg-[#d9d9d9] p-4">
                <PrismicNextImage
                  field={client_logo}
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                />
              </div>
            )}
          </div>
        </div>

        {/* Profile Card Section */}
        <div
          ref={profileCardRef}
          className="w-full max-w-[1040px] rounded-[16px] p-6 shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] opacity-0 translate-y-8 transition-all duration-700 ease-out"
          style={{
            background:
              'linear-gradient(to bottom, rgba(0, 39, 38, 0.8) 0%, rgba(0, 0, 0, 0) 100%)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
        >
          <div className="flex flex-col md:flex-row gap-6 items-center">
            {/* Profile Info - Left Side */}
            {profile && (
              <div className="flex flex-col gap-6 items-center justify-center w-full md:w-[200px] shrink-0">
                {/* Avatar */}
                {isFilled.image(profile.avatar) && (
                  <div className="relative w-[130px] h-[130px] rounded-[30px] overflow-hidden">
                    <PrismicNextImage
                      field={profile.avatar}
                      className="object-cover w-full h-full"
                      fill
                    />
                  </div>
                )}

                {/* Name, Role, Email */}
                <div className="flex flex-col gap-[9px] items-center justify-center text-white text-center w-full">
                  {profile.name && (
                    <p className="font-bold text-[20px] leading-[1.2] capitalize">
                      {profile.name}
                    </p>
                  )}
                  <div className="font-bold text-[16px] leading-[1.4]">
                    {profile.role && <p>{profile.role}</p>}
                    {profile.email && <p>{profile.email}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Vertical Divider */}
            <div className="hidden md:block w-px h-[330px] bg-gradient-to-b from-transparent via-white/20 to-transparent" />

            {/* Horizontal Divider for mobile */}
            <div className="block md:hidden w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

            {/* Greeting Content - Right Side */}
            <div className="flex flex-col gap-6 items-start flex-1 min-w-0">
              {/* Greeting Heading */}
              {isFilled.richText(heading) && (
                <div className="font-medium text-[24px] md:text-[30px] leading-[1.2] text-white capitalize">
                  <PrismicRichText field={heading} />
                </div>
              )}

              {/* Description */}
              {isFilled.richText(description) && (
                <div className="font-light text-[14px] md:text-[16px] leading-[1.4] text-white">
                  <PrismicRichText field={description} />
                </div>
              )}

              {/* CTA Buttons */}
              {ctas && ctas.length > 0 && (
                <div className="flex flex-wrap gap-[10px] items-start">
                  {ctas.map((cta, index) => {
                    const isPrimary = index === 0;

                    return (
                      <PrismicNextLink
                        key={index}
                        field={cta.link}
                        className={`px-6 py-3 rounded-full font-bold text-[16px] leading-[1.5] transition-all ${
                          isPrimary
                            ? 'border border-[#05f273] text-[#05f273] hover:bg-[#05f273]/10'
                            : 'bg-[rgba(234,234,234,0.3)] text-white hover:bg-[rgba(234,234,234,0.4)]'
                        }`}
                      >
                        {cta.label}
                      </PrismicNextLink>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfileGreetingCta;
